import { Component, OnDestroy, OnInit, computed, signal } from "@angular/core";
import {
  FormGroup
} from "@angular/forms";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, take } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ChequeSyncService } from "../../services/cheque-sync.service";
import { DataService } from "../../services/data.service";
import { DomainService } from "../../services/domain.service";
import { OrderService } from "../../services/order.service";
import { PayAtTablePaymentService } from "../../services/pay-at-table-payment.service";
import { TableSocketService } from "../../services/table-socket.service";
import { PaymentOptionResult } from "../../shared/payment-options-sheet/payment-options-sheet";
import { PaymentMethod } from "../../models/cart.model";
import { PayAtTableFee, PayAtTableOrder, PayAtTableCheque, PayAtTableCalculatePayload, PayAtTableNiPayload, PayAtTablePaymentMode, PayAtTableResponse } from "../../models/pay-at-table.model";
import { AppliedPromo } from "../../models/voucher.model";
import { isChequeEvent, SocketEventType } from "../../models/websocket.model";
import { ApiService } from "../../services/api.service";
import { ToastService } from "../../services/toast.service";

type SplitType = "evenly" | "custom" | "items";

export type OrderLine = {
  id: string;
  orderItemId: string;
  name: string;
  qty: number;
  price: number;
};

@Component({
  selector: "app-split-payment-page",
  templateUrl: "./split-payment.html",
  styleUrls: ["./split-payment.scss"],
  standalone: false
})
export class SplitPaymentPageComponent implements OnInit, OnDestroy {

  public currency = signal("AED");
  public billAmount = signal(0);
  public totalRemaining = signal(0);
  public amountToPay = signal(0);
  public hasCalculated = signal(false);
  public maxPayers = signal<number | null>(null);
  public currentPayers = signal<number | null>(null);
  private chequeId = signal<string | null>(null);

  public subtotal = signal(0);
  public discount = signal(0);
  public appliedPromo = signal<AppliedPromo | null>(null);
  public serviceFee = signal(0);
  public fees = signal<PayAtTableFee[]>([]);

  // Split type from query param (?type=evenly|custom|items)
  public splitType = signal<SplitType>("evenly");

  // ---- Evenly state ----
  public totalPeople = signal(1);
  public payingFor = signal(0);

  // ---- Custom state ----
  public customAmount = signal(0);

  // ---- Items state ----
  public items = signal<OrderLine[]>([]);

  // Selected quantities per line for "pay for your items"
  public selectedQty = signal<Record<string, number>>({});
  public isLoading = signal(false);
  // Tracks whether evenly/items selection changed since the last calculate() call
  public hasPendingSelection = signal(false);
  public paymentMethods: PaymentMethod[] = [];
  public paymentType: PaymentMethod | undefined;

  public form = new FormGroup({});

  // Cheque payment lock (PAT venues with pat_cheque_sync_enabled)
  public lockedByOther = computed(() => this.chequeSyncService.lockedByOther());

  // Always entered from the payments page; a fixed destination sidesteps
  // history.back()/gateway-redirect quirks entirely instead of trying to out-guess them.
  public goToPayments = (): void => {
    this.router.navigate(['/pay-at-table']);
  };

  private chequeUpdateSubscription: Subscription;

  // ----------  totals ----------
  public evenlyYourTotal = computed(() => {
    const base = this.amountToPay();
    if (base > 0) {
      return base;
    }
    return Math.max(0, this.subtotal() - this.discount() + this.serviceFee());
  });

  public customYourTotal = computed(() => {
    //subtotal becomes entered amount; then + service fee
    const base = this.amountToPay();
    if (base > 0) {
      return base;
    }
    const sub = Math.max(0, Number(this.customAmount()) || 0);
    return sub + this.serviceFee();
  });

  public itemsSubtotal = computed(() => {
    const map = this.selectedQty();
    return this.items().reduce((sum, it) => {
      const q = Math.max(0, Math.min(it.qty, map[it.id] ?? 0));
      return sum + q * it.price;
    }, 0);
  });

  public itemsYourTotal = computed(() => {
    const base = this.amountToPay();
    if (base > 0) {
      return base;
    }
    return 0;
  });
  public pageTitle = computed(() => {
    switch (this.splitType()) {
      case "custom":
        return "PAY_AT_TABLE.CUSTOM_AMOUNT";
      case "items":
        return "PAY_AT_TABLE.PAY_FOR_ITEMS";
      default:
        return "PAY_AT_TABLE.SPLIT_EVENLY";
    }
  });

  public displaySubtotal = computed(() => {
    return this.subtotal();
  });

  public displayYourTotal = computed(() => {
    if (this.amountToPay() <= 0) {
      return 0;
    }
    if (this.splitType() === "custom") return this.customYourTotal();
    if (this.splitType() === "items") return this.itemsYourTotal();
    return this.evenlyYourTotal();
  });
  public isOrderFree = computed(() => this.totalWithTip() <= 0);

  public remainingBalance = computed(() => {
    const amount = this.amountToPay();
    if (amount < 0) {
      return Math.abs(amount);
    }
    return 0;
  });

  isPayDisabled = computed(() =>
    (this.totalWithTip() <= 0 && this.splitType() === 'items') || this.hasPendingSelection()
  );

  // tipping
  tipAmount = signal<number>(0);
  totalWithTip = computed(() => this.displayYourTotal() + (this.tipAmount() ?? 0));

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private apiService: ApiService,
    private dataService: DataService,
    private domainService: DomainService,
    private payAtTablePaymentService: PayAtTablePaymentService,
    private chequeSyncService: ChequeSyncService,
    private orderService: OrderService,
    private tableSocketService: TableSocketService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    // init type from query params
    const type = (this.route.snapshot.queryParamMap.get("type") ??
      "evenly") as SplitType;
    this.splitType.set(this.normalizeType(type));

    const init: Record<string, number> = {};
    for (const it of this.items()) init[it.id] = 0;
    this.selectedQty.set(init);

    this.initPaymentMethods();
    this.loadPayAtTableOrders();
    this.chequeSyncService.enterPaymentFlow();

    this.chequeUpdateSubscription = new Subscription();

    // Keeps this guest's cheque in sync with another guest's item/payment updates
    // while this page is open. Debounced so a partial payment followed shortly by
    // a closing one doesn't reload twice. Note: reloading resets any in-progress
    // split selection back to its defaults.
    this.chequeUpdateSubscription.add(
      this.tableSocketService.message$.pipe(
        filter((event) => isChequeEvent(event) && event.type !== SocketEventType.ChequeClosed && event.cheque_id === this.chequeId()),
        debounceTime(300),
      ).subscribe(() => this.loadPayAtTableOrders())
    );

    // Someone else fully paid the cheque — nothing left to split here, back to the main page.
    this.chequeUpdateSubscription.add(
      this.tableSocketService.message$.pipe(
        filter((event) => event.type === SocketEventType.ChequeClosed && event.cheque_id === this.chequeId()),
      ).subscribe(() => this.close())
    );
  }

  ngOnDestroy(): void {
    this.chequeUpdateSubscription?.unsubscribe();
    this.chequeSyncService.leavePaymentFlow();
  }

  private normalizeType(t: string): SplitType {
    if (t === "custom") return "custom";
    if (t === "items") return "items";
    return "evenly";
  }

  public loadPayAtTableOrders(): void {
    this.isLoading.set(true);
    const voucherName = this.dataService.voucherName();
    this.payAtTablePaymentService
      .applyPostpaidCheckoutThenLoadOrders(voucherName || undefined)
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (res) {
            this.applyOrders(res.orders);
            this.applyCheque(res.cheque);
            if (this.hasConflictingPaymentMode(res.cheque)) {
              this.close(); // someone locked in a different split mode — this flow no longer applies
              return;
            }
            this.calculate();
          }
        },
        error: () => this.isLoading.set(false),
      });
  }

  // Two guests can each pick a different split option before either pays — once one
  // of them locks in a mode on the cheque, the other's choice no longer sticks.
  private hasConflictingPaymentMode(cheque: PayAtTableCheque | null | undefined): boolean {
    const lockedMode = cheque?.payment_mode ?? null;
    const ourMode = this.getPaymentMode();
    return !!lockedMode && !!ourMode && lockedMode !== ourMode;
  }

  private applyOrders(orders: PayAtTableOrder[]): void {
    // this.fees.set(orders?.[0]?.fees ?? []);
    const items: OrderLine[] = [];
    orders.forEach((order) => {
      order.items.forEach((item) => {
        items.push({
          id: item.id,
          orderItemId: item.order_item_id ?? item.id,
          name: item.item,
          qty: item.quantity,
          price: item.unit_price,
        });
      });
    });

    this.items.set(items);
    const init: Record<string, number> = {};
    for (const it of items) init[it.id] = 0;
    this.selectedQty.set(init);

    const currency = orders?.[0]?.currency;
    if (currency) {
      this.currency.set(currency);
    }
  }

  private applyCheque(cheque: PayAtTableCheque | null | undefined): void {
    this.chequeId.set(cheque?.id ?? null);

    if (!cheque) {
      this.maxPayers.set(null);
      this.currentPayers.set(null);
      this.totalPeople.set(1);
      this.payingFor.set(0);
      return;
    }

    const hasMaxPayers =
      cheque.max_payers !== null && cheque.max_payers !== undefined;
    const rawMaxPayers = hasMaxPayers ? Number(cheque.max_payers) : null;
    const maxPayers =
      rawMaxPayers !== null && Number(rawMaxPayers)
        ? rawMaxPayers
        : null;
    this.maxPayers.set(maxPayers);
    this.currentPayers.set(
      cheque.current_payers !== null && cheque.current_payers !== undefined
        ? Number(cheque.current_payers)
        : null
    );

    this.totalPeople.set(maxPayers ?? 1);
    this.payingFor.set(0);

    if (cheque.items?.length) {
      const items: OrderLine[] = cheque.items.map((item) => ({
        id: item.item_id,
        orderItemId: item.item_id,
        name: item.name,
        qty: item.quantity,
        price: item.price,
      }));
      this.items.set(items);
      const init: Record<string, number> = {};
      for (const it of items) init[it.id] = 0;
      this.selectedQty.set(init);
    }
  }

  // ----------  actions ----------
  close(): void {
    this.location.back();
  }

  onPaymentOptionSelected(selected: PaymentOptionResult): void {
    if (this.guardLockedByOther()) return;
    if (!selected) return;

    const payload = this.buildPaymentPayload();
    if (!payload) return;

    const amount = Number(this.totalWithTip() ?? 0);
    const currency = this.currency() || "AED";

    this.paymentType = selected;

    const isWallet =
      this.paymentType?.key === "applepay" || this.paymentType?.key === "googlepay";
    const voucherName = this.dataService.voucherName();

    if (isWallet) {
      this.chequeSyncService.markPaymentInFlight(true);
      this.payAtTablePaymentService.startPayment(
        payload,
        this.paymentType,
        amount,
        currency,
        (value) => this.isLoading.set(value),
        voucherName || undefined
      );
      return;
    }

    this.isLoading.set(true);
    this.chequeSyncService.markPaymentInFlight(true);
    this.payAtTablePaymentService
      .confirmPostpaidCheckoutWithPayAtTablePayload(payload, voucherName || undefined)
      .subscribe((res) => {
        if (!res) {
          this.chequeSyncService.markPaymentInFlight(false);
          this.isLoading.set(false);
          return;
        }
        this.payAtTablePaymentService.startPayment(
          payload,
          this.paymentType,
          amount,
          currency,
          (value) => this.isLoading.set(value)
        );
      });
  }

  private guardLockedByOther(): boolean {
    if (!this.chequeSyncService.lockedByOther()) {
      return false;
    }
    this.toastService.warning(this.translateService.instant("PAY_AT_TABLE.SOMEONE_ELSE_PAYING"));
    return true;
  }

  private buildSplitPayload(
    tableId: string,
    userId: string
  ): PayAtTableCalculatePayload | null {
    const paymentMode = this.getPaymentMode();
    if (!paymentMode) {
      return null;
    }
    const voucherName = this.dataService.voucherName();

    return {
      table_id: tableId,
      user_id: userId,
      voucher_name: voucherName || undefined,
      payment_mode: paymentMode,
      payment_details: this.buildPaymentDetails(paymentMode, true),
    };
  }

  private buildPaymentPayload(): PayAtTableNiPayload | null {
    const tableId = this.dataService.table()?.id;
    if (!tableId) return null;

    const paymentMode = this.getPaymentMode();
    if (!paymentMode) return null;

    return {
      table_id: tableId,
      subdomain: this.domainService.getSubdomain(),
      user_id: this.dataService.getUserId(),
      payment_mode: paymentMode,
      payment_details: this.buildPaymentDetails(paymentMode, false),
      tip_amount: this.tipAmount() ?? 0,
    };
  }

  private buildPaymentDetails(
    paymentMode: PayAtTablePaymentMode,
    itemIdFallbackToId: boolean
  ): PayAtTableCalculatePayload["payment_details"] {
    if (paymentMode === "split") {
      return {
        total_people: this.getTotalPeopleForPayload(),
        paying_for: this.getPayingForForPayload(),
      };
    }

    if (paymentMode === "custom_amount") {
      return {
        amount: Number(this.customAmount() || 0),
      };
    }

    const selected = this.selectedQty();
    const orderItems = this.items()
      .map((item) => ({
        item_id: itemIdFallbackToId ? item.orderItemId ?? item.id : item.orderItemId,
        quantity: Math.max(0, Math.min(item.qty, selected[item.id] ?? 0)),
      }))
      .filter((item) => item.quantity > 0);

    return { order_items: orderItems };
  }

  private getPaymentMode(): PayAtTablePaymentMode | null {
    if (this.splitType() === "evenly") return "split";
    if (this.splitType() === "custom") return "custom_amount";
    if (this.splitType() === "items") return "item_selection";
    return null;
  }

  private getRemainingPayers(): number | null {
    const max = this.maxPayers();
    const current = this.currentPayers();
    if (max === null || current === null) return null;
    return Math.max(0, max - current);
  }

  private getTotalPeopleForPayload(): number {
    const max = this.maxPayers();
    const value = this.totalPeople();
    if (max === null || Number.isNaN(max)) return value;
    return Math.min(value, max);
  }

  private getPayingForForPayload(): number {
    const remaining = this.getRemainingPayers();
    const value = this.payingFor();
    if (remaining === null) return value;
    return Math.min(value, remaining);
  }

  private applyCalculationResponse(res: PayAtTableResponse): void {
    this.isLoading.set(false);
    // this.fees.set(res.orders?.[0]?.fees ?? []);
    const cheque = res.cheque;
    if (!cheque) {
      return;
    }

    const isAnySelected = Object.values(this.selectedQty()).some((v) => !!v) || !(this.splitType() === "items");

    const amountToPay = isAnySelected ? Math.max(0, Number(cheque.amount_to_pay || 0)) : 0;
    const discount = isAnySelected ? Number(cheque.discount || 0) : 0;
    const promo = isAnySelected ? (cheque.promo ?? null) : null;
    const fees = isAnySelected ? Number(cheque.fees || 0) : 0;
    const totalAmount = Number(cheque.original_amount);
    const subTotal = isAnySelected ? Number(cheque.subtotal ?? 0) : 0;
    const totalRemaining = Number(
      cheque.remaining_amount_without_discount ??
      cheque.remaining_amount ??
      0
    );

    this.billAmount.set(totalAmount);
    this.subtotal.set(subTotal);
    this.totalRemaining.set(totalRemaining);
    this.amountToPay.set(amountToPay);
    this.discount.set(discount);
    this.appliedPromo.set(promo);
    this.serviceFee.set(fees);
    this.hasCalculated.set(true);

  }

  private initPaymentMethods(): void {
    const methods = this.dataService.paymentMethods();
    if (!methods?.length) return;

    this.paymentMethods = this.payAtTablePaymentService.filterOutUnavailableApplePay(methods);
    this.paymentType = this.paymentMethods[0];

    this.payAtTablePaymentService.filterOutUnavailableGooglePay(this.paymentMethods).then((filtered) => {
      if (!filtered) return;
      this.paymentMethods = filtered;
      if (this.paymentType?.key === "googlepay") {
        this.paymentType = this.paymentMethods[0];
      }
    });
  }

  // ---------- Evenly steppers ----------
  incTotalPeople(): void {
    const max = this.maxPayers();
    if (max !== null && !Number.isNaN(max) && this.totalPeople() >= max) return;

    this.totalPeople.update((v) => v + 1);
    this.hasPendingSelection.set(true);
  }

  decTotalPeople(): void {
    if (this.totalPeople() <= 1) return;

    this.totalPeople.update((v) => v - 1);
    this.payingFor.update((v) => Math.min(v, this.totalPeople()));
    this.hasPendingSelection.set(true);
  }

  incPayingFor(): void {
    const remaining = this.getRemainingPayers();
    if (remaining !== null && this.payingFor() >= remaining) return;
    if (this.payingFor() >= this.totalPeople()) return;

    this.payingFor.update((v) => v + 1);
    this.hasPendingSelection.set(true);
  }

  decPayingFor(): void {
    if (this.payingFor() <= 0) return;

    this.payingFor.update((v) => v - 1);
    this.hasPendingSelection.set(true);
  }

  // ---------- Items steppers ----------
  incItem(id: string): void {
    const it = this.items().find((x) => x.id === id);
    if (!it) return;

    const current = this.selectedQty()[id] ?? 0;
    if (current >= it.qty) return;

    this.selectedQty.update((map) => ({ ...map, [id]: current + 1 }));
    this.hasPendingSelection.set(true);
  }

  decItem(id: string): void {
    this.selectedQty.update((map) => {
      const current = map[id] ?? 0;
      const next = Math.max(0, current - 1);
      return { ...map, [id]: next };
    });

    this.hasPendingSelection.set(true);
  }

  // ---------- Confirm selection (evenly / items) ----------
  confirmSelection(): void {
    if (!this.hasPendingSelection()) return;

    this.hasPendingSelection.set(false);
    this.calculate();
  }

  public calculate(): void {
    const tableId = this.dataService.table()?.id;
    const userId = this.dataService.getUserId();
    const restaurantId = this.dataService.restaurant().id;
    if (!tableId || !userId || !restaurantId) {
      return;
    }

    const payload = this.buildSplitPayload(tableId, userId);
    if (!payload) {
      return;
    }

    this.isLoading.set(true);

    this.apiService
      .getPayAtTableOrders(restaurantId, payload)
      .pipe(take(1))
      .subscribe({
        next: (res) => this.applyCalculationResponse(res),
        error: () => this.isLoading.set(false),
      });
  }

  onTipApplied(data: { tip_amount: number }): void {
    this.tipAmount.set(data.tip_amount);
  }
}
